/* ==============================
   CWC Article
   ------------------------------
   Tidies the markup of older posts (pasted from Blogger, Word and email
   builders) after the stylesheet has evened out fonts and colours:
   - hides blocks that only hold spaces or line breaks,
   - turns <br> stacked for spacing into one even paragraph gap,
   - hides the copy of the featured image that old posts repeat in the text,
   - asks Blogger for a sharper copy of its small thumbnails,
   - hides an image that fails to load instead of showing a broken icon,
   - gives short bold lines (the old posts' headings) the subheading style,
   - marks "Q:" lines and the FDA footnote so they read as such.
   Clean posts are left as they are.
   ============================== */
(function () {
  'use strict';

  var MEDIA = 'img,iframe,video,table,hr,svg,picture,object,embed,input,button';
  var BLOCKS = 'div,p,h1,h2,h3,h4,h5,h6,ul,ol,table,blockquote,figure';

  function bare(text) {
    return (text || '').replace(/[\s ​﻿]+/g, '');
  }

  function isLeaf(el) {
    return !el.querySelector(BLOCKS);
  }

  function hide(el) {
    el.classList.add('cwc_article__empty');
  }

  function hideImage(img) {
    var link = img.closest('a');
    var box = link && bare(link.textContent) === '' ? link : img;
    var wrap = box.closest('.separator, .tr-caption-container');
    hide(wrap || box);
  }

  // Blogger thumbnails carry their size in the URL (/s320/ or =s320).
  function sharperBlogger(src) {
    if (!/(bp\.blogspot\.com|googleusercontent\.com)/.test(src)) return src;
    return src
      .replace(/\/s\d{2,4}(-[a-z0-9-]+)?\//i, '/s1200/')
      .replace(/=s\d{2,4}(-[a-z0-9-]+)?$/i, '=s1200');
  }

  // Names for the same picture across Shopify and Blogger copies: drops the folder,
  // query, extension, URL encoding, a "(1)" copy mark and Shopify's uuid and size
  // suffixes. The second name also drops a short counter ("_1", " 2"), which
  // Shopify adds to a re-uploaded file and some old names carry.
  function imageKeys(url) {
    var name = String(url || '').split('?')[0].split('/').pop();
    for (var i = 0; i < 3; i++) {
      try {
        var decoded = decodeURIComponent(name);
        if (decoded === name) break;
        name = decoded;
      } catch (e) {
        break;
      }
    }
    var base = name
      .toLowerCase()
      .replace(/\.(jpe?g|png|gif|webp|avif)$/, '')
      .replace(/[\s+_-]*\(\d+\)$/, '')
      .replace(/_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/, '')
      .replace(/_(\d+x\d*|\d*x\d+)$/, '');
    var keys = [base.replace(/[^a-z0-9]/g, ''), base.replace(/[\s+_-]\d{1,2}$/, '').replace(/[^a-z0-9]/g, '')];
    return keys.filter(function (key) {
      return key !== '';
    });
  }

  function sameImage(keysA, keysB) {
    return keysA.some(function (key) {
      return keysB.indexOf(key) !== -1;
    });
  }

  function tidyImages(body) {
    // The featured image already heads the page; old posts repeat it in the text
    var featured = imageKeys(body.getAttribute('data-cwc-featured'));

    body.querySelectorAll('img').forEach(function (img) {
      var original = img.getAttribute('src');
      if (!original || sameImage(imageKeys(original), featured)) {
        hideImage(img);
        return;
      }

      var sharper = sharperBlogger(original);
      if (sharper !== original) {
        img.setAttribute('data-cwc-sharper', '1');
        img.removeAttribute('width');
        img.removeAttribute('height');
        img.setAttribute('src', sharper);
      } else if (img.complete && img.naturalWidth === 0) {
        hideImage(img);
        return;
      }

      // A sharper copy that fails falls back to the original; an original that
      // fails is hidden.
      img.addEventListener('error', function onError() {
        if (img.getAttribute('data-cwc-sharper') === '1') {
          img.setAttribute('data-cwc-sharper', '0');
          img.setAttribute('src', original);
          return;
        }
        img.removeEventListener('error', onError);
        hideImage(img);
      });
    });
  }

  var INLINE = 'span, font, b, strong, i, em, u, a, small, big, sup, sub';
  // classes of posts written for this template (cwc-lead, cwc-note, ...)
  var CLEAN = '[class^="cwc-"], [class*=" cwc-"]';

  function isBlank(node) {
    return node.nodeType === 3 && bare(node.textContent) === '';
  }

  function nextBr(node) {
    var next = node.nextSibling;
    while (next && isBlank(next)) next = next.nextSibling;
    return next && next.nodeName === 'BR' ? next : null;
  }

  function textAfter(node, container) {
    var range = document.createRange();
    range.setStartAfter(node);
    range.setEnd(container, container.childNodes.length);
    return bare(range.toString()) !== '';
  }

  function tidySpacing(body) {
    // Two or more <br> in a row were a paragraph break. They become one empty block
    // with a bottom margin, which merges with a paragraph's own margin instead of
    // adding to it.
    body.querySelectorAll('br').forEach(function (br) {
      if (!br.isConnected || !nextBr(br)) return;
      var run = [br];
      for (var next = nextBr(br); next; next = nextBr(next)) run.push(next);
      var gap = document.createElement('span');
      gap.className = 'cwc_article__gap';
      br.before(gap);
      run.forEach(function (b) {
        b.remove();
      });
    });

    // Blocks with nothing to show; deepest first so empty parents follow.
    // An inline wrapper around a lone <br> stays: that <br> is a line break.
    var blocks = body.querySelectorAll('div, p, span, font, b, strong, i, em, u, center, section, h1, h2, h3, h4, h5, h6');
    for (var i = blocks.length - 1; i >= 0; i--) {
      var el = blocks[i];
      if (bare(el.textContent) !== '' || el.querySelector(MEDIA) || el.classList.contains('cwc_article__gap')) continue;
      if (el.matches(INLINE) && el.querySelector('br, .cwc_article__gap')) continue;
      hide(el);
    }

    // A <br> that starts a line (after a block, a gap or a hidden image) only adds an empty line
    body.querySelectorAll('br').forEach(function (br) {
      if (endsLine(br, 'previousSibling')) br.remove();
    });

    // Old blocks that hold their text directly are paragraphs, whatever their markup
    body.querySelectorAll('div, p').forEach(function (el) {
      if (el.classList.contains('cwc_article__empty') || el.closest(CLEAN)) return;
      for (var node = el.firstChild; node; node = node.nextSibling) {
        var isText = node.nodeType === 3 || (node.nodeType === 1 && node.matches(INLINE));
        if (isText && bare(node.textContent) !== '') {
          el.classList.add('cwc_article__para');
          return;
        }
      }
    });
  }

  function markLines(body) {
    var leaves = Array.prototype.filter.call(body.querySelectorAll('div, p, h5, h6'), function (el) {
      return isLeaf(el) && !el.classList.contains('cwc_article__empty');
    });

    leaves.forEach(function (el, index) {
      var text = (el.textContent || '').replace(/\u00a0/g, ' ').trim();

      if (/^Q\s*[:.]\s*\S/.test(text) && text.length < 220) {
        el.classList.add('cwc_article__q');
        el.classList.remove('cwc_article__para');
      } else if (/^A\s*:$/.test(text)) {
        hide(el);
      }

      if (/evaluated by the Food and Drug Administration/i.test(text)) {
        el.classList.add('cwc-disclaimer');
        el.classList.remove('cwc_article__para');
        var next = leaves[index + 1];
        if (next && /prevent any disease|results may vary/i.test(next.textContent || '')) {
          next.classList.add('cwc-disclaimer');
          next.classList.remove('cwc_article__para');
        }
      }
    });
  }

  function display(el) {
    return getComputedStyle(el).display;
  }

  // Nodes that show nothing: spaces, comments, hidden blocks, inline wrappers left empty
  function skippable(node) {
    if (isBlank(node) || node.nodeType === 8) return true;
    if (node.nodeType !== 1 || node.nodeName === 'BR') return false;
    var shown = display(node);
    if (shown === 'none') return true;
    return shown === 'inline' && bare(node.textContent) === '' && !node.querySelector(MEDIA + ',br');
  }

  // True when only a line break, a block or the edge of the block sits on this side of the node
  function endsLine(node, step) {
    for (var cur = node; cur; ) {
      var sib = cur[step];
      while (sib && skippable(sib)) sib = sib[step];
      if (sib) {
        if (sib.nodeName === 'BR') return true;
        if (sib.nodeType !== 1) return false;
        return display(sib) !== 'inline';
      }
      cur = cur.parentElement;
      if (!cur || display(cur) !== 'inline') return true;
    }
    return true;
  }

  // Old posts set their headings as a short bold line of their own: style those as subheadings
  function markSubheads(body) {
    body.querySelectorAll('b, strong').forEach(function (b) {
      if (b.closest('a, li, td, th, h1, h2, h3, h4, h5, h6, .cwc_article__subhead, .cwc_article__q, .cwc_article__empty') || b.closest(CLEAN)) return;

      var text = (b.textContent || '').replace(/\u00a0/g, ' ').trim();
      if (text.length < 4 || text.length > 90 || /[.,;!]$/.test(text)) return;

      // bold text that runs over several lines is an emphasised paragraph, not a heading
      var breaks = b.querySelectorAll('br, .cwc_article__gap');
      for (var i = 0; i < breaks.length; i++) {
        if (textAfter(breaks[i], b)) return;
      }

      var line = b;
      while (line.parentElement && line.parentElement !== body && line.parentElement.matches(INLINE) && bare(line.parentElement.textContent) === bare(b.textContent)) {
        line = line.parentElement;
      }
      if (!endsLine(line, 'previousSibling') || !endsLine(line, 'nextSibling')) return;

      // A block that holds nothing but this line becomes the subheading itself
      var parent = line.parentElement;
      if (parent && parent !== body && parent.matches('div, p') && bare(parent.textContent) === bare(b.textContent) && !parent.querySelector(MEDIA)) {
        line = parent;
      }
      line.classList.add('cwc_article__subhead');
      line.classList.remove('cwc_article__para');

      // The line break after the heading would now open an empty line
      var next = line.nextSibling;
      while (next && isBlank(next)) next = next.nextSibling;
      if (next && (next.nodeName === 'BR' || (next.nodeType === 1 && next.classList.contains('cwc_article__gap')))) next.remove();
    });
  }

  function tidy(body) {
    if (body.getAttribute('data-cwc-tidied')) return;
    body.setAttribute('data-cwc-tidied', '1');
    tidyImages(body);
    tidySpacing(body);
    markLines(body);
    markSubheads(body);
  }

  function run() {
    document.querySelectorAll('[data-cwc-article-body]').forEach(tidy);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // Theme editor re-renders the section without a page load
  document.addEventListener('shopify:section:load', run);
})();
